import { endpoint } from "./config";
import { ConversionRequestBodySchema, ConversionRequestSchema, ProductErrorSchema, type ConversionRequest, type ProductError } from "@urlcade/contracts";

export type ConversionStatusDto = ConversionRequest;

export interface CreateConversionInput {
  url: string;
  locale: "tr-TR" | "en-US";
  captureProfile: "desktop-page";
  idempotencyKey: string;
}

export function normalizeUserUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) throw new Error("INVALID_URL");
  if (trimmed.length > 4096) throw new Error("URL_TOO_LONG");
  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try { parsed = new URL(candidate); } catch { throw new Error("INVALID_URL"); }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") throw new Error("UNSUPPORTED_SCHEME");
  if (!parsed.hostname || parsed.username || parsed.password) throw new Error("INVALID_URL");
  return parsed.toString();
}

export class ConversionService {
  constructor(private readonly getAccessToken: () => Promise<string>) {}

  async create(input: Omit<CreateConversionInput, "idempotencyKey">, signal?: AbortSignal): Promise<string> {
    const token = await this.getAccessToken();
    const payload = ConversionRequestBodySchema.parse({ ...input, idempotencyKey: crypto.randomUUID() });
    const body = await this.request("/api/conversions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, Accept: "application/json" },
      body: JSON.stringify(payload),
      signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(12_000)]) : AbortSignal.timeout(12_000),
    });
    const parsed = ConversionRequestSchema.safeParse(body);
    if (!parsed.success) throw new Error("CONVERSION_RESPONSE_INVALID");
    return parsed.data.id;
  }

  async get(requestId: string, signal?: AbortSignal): Promise<ConversionStatusDto> {
    const token = await this.getAccessToken();
    const body = await this.request(`/api/conversions/${encodeURIComponent(validateRequestId(requestId))}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(10_000)]) : AbortSignal.timeout(10_000),
    });
    const parsed = ConversionRequestSchema.safeParse(body);
    if (!parsed.success || parsed.data.id !== requestId) throw new Error("CONVERSION_RESPONSE_INVALID");
    return parsed.data;
  }

  async cancel(requestId: string): Promise<void> {
    const token = await this.getAccessToken();
    const body = await this.request(`/api/conversions/${encodeURIComponent(validateRequestId(requestId))}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, Accept: "application/json" },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(10_000),
    });
    const parsed = ConversionRequestSchema.safeParse(body);
    if (!parsed.success || parsed.data.id !== requestId) throw new Error("CONVERSION_RESPONSE_INVALID");
  }

  async retry(requestId: string): Promise<string> {
    const token = await this.getAccessToken();
    const body = await this.request(`/api/conversions/${encodeURIComponent(validateRequestId(requestId))}/retry`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, Accept: "application/json" },
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
      signal: AbortSignal.timeout(10_000),
    });
    const parsed = ConversionRequestSchema.safeParse(body);
    if (!parsed.success) throw new Error("CONVERSION_RESPONSE_INVALID");
    return parsed.data.id;
  }

  private async request(path: string, init: RequestInit): Promise<unknown> {
    let response: Response;
    try { response = await fetch(endpoint(path), init); }
    catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new Error("NETWORK_UNAVAILABLE");
    }
    let body: unknown;
    try { body = await response.json(); } catch { body = null; }
    if (!response.ok) {
      const error = ProductErrorSchema.safeParse(body);
      if (error.success) throw new ConversionApiError(error.data);
      const nested = asRecord(body)?.error;
      const parsedNested = ProductErrorSchema.safeParse(nested);
      if (parsedNested.success) throw new ConversionApiError(parsedNested.data);
      throw new Error(`HTTP_${response.status}`);
    }
    return body;
  }
}

function validateRequestId(value: string): string {
  if (!/^[0-9a-f-]{36}$/i.test(value)) throw new Error("CONVERSION_ID_INVALID");
  return value;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

export class ConversionApiError extends Error {
  constructor(readonly productError: ProductError) { super(productError.code); this.name = "ConversionApiError"; }
}
